import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import type { Character } from '@gm-binder/shared';
import {
  PageHeader,
  Card,
  Badge,
  IconButton,
  MarkdownView,
  StatBlock,
  MediaImage,
  Switch,
} from '../ui';
import { useBinder } from '../../lib/useBinder';
import { useRunStore } from '../../store/runStore';
import { ShowButton } from '../common/ShowButton';
import { Backlinks } from '../common/Backlinks';
import { mediaUrl } from '../../api/client';
import shared from './sections.module.css';
import styles from './CharactersSection.module.css';

function maxHp(c: Character): number {
  return c.hitPoints?.max ?? 0;
}

/** Pre-generated PC roster with live HP and party-active toggles. */
export function CharactersSection() {
  const ctx = useBinder();
  const setHp = useRunStore((s) => s.setHp);
  const toggleActive = useRunStore((s) => s.toggleActiveCharacter);
  if (!ctx) return null;
  const { adventure, state, instance, slug } = ctx;

  return (
    <div>
      <PageHeader eyebrow="Personaggi" title="Personaggi giocanti" />
      <div className={shared.grid}>
        {adventure.characters.map((c) => {
          const max = maxHp(c);
          const current = state.hp[c.name] ?? c.hitPoints?.current ?? max;
          const inactive = state.inactiveCharacters.includes(c.name);
          return (
            <Card key={c.name} padded={false} className={styles.card}>
              <Link to={`/run/${instance.id}/characters/${encodeURIComponent(c.name)}`}>
                <MediaImage
                  src={mediaUrl(slug, c.image)}
                  alt={c.name}
                  fallback="🛡️"
                  className={styles.portrait}
                />
              </Link>
              <div className={styles.cardBody}>
                <div className={styles.cardTop}>
                  <Link
                    to={`/run/${instance.id}/characters/${encodeURIComponent(c.name)}`}
                    className={styles.name}
                  >
                    {c.name}
                  </Link>
                  <ShowButton slug={slug} image={c.image} title={c.name} instanceId={instance.id} />
                </div>
                <div className={shared.entityMeta}>
                  {c.race} {c.class} · Lv {c.level ?? 1}
                </div>
                <HpControl
                  current={current}
                  max={max}
                  onChange={(v) => setHp(c.name, v)}
                />
                <div className={styles.activeRow}>
                  <Switch
                    checked={!inactive}
                    onChange={() => toggleActive(c.name)}
                    label={inactive ? 'In panchina' : 'In gioco'}
                  />
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function HpControl({
  current,
  max,
  onChange,
}: {
  current: number;
  max: number;
  onChange: (v: number) => void;
}) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (current / max) * 100)) : 0;
  const tone = pct > 50 ? 'var(--color-success)' : pct > 25 ? 'var(--color-warn)' : 'var(--color-ember)';
  return (
    <div className={styles.hp}>
      <div className={styles.hpButtons}>
        <IconButton label="-5" size="sm" onClick={() => onChange(Math.max(0, current - 5))}>
          −5
        </IconButton>
        <IconButton label="-1" size="sm" onClick={() => onChange(Math.max(0, current - 1))}>
          −1
        </IconButton>
        <span className={styles.hpValue}>
          {current}
          <span className={styles.hpMax}>/{max}</span>
        </span>
        <IconButton label="+1" size="sm" onClick={() => onChange(Math.min(max, current + 1))}>
          +1
        </IconButton>
        <IconButton label="+5" size="sm" onClick={() => onChange(Math.min(max, current + 5))}>
          +5
        </IconButton>
      </div>
      <div className={styles.hpBar}>
        <div className={styles.hpFill} style={{ width: `${pct}%`, background: tone }} />
      </div>
    </div>
  );
}

/** Full character sheet. */
export function CharacterDetail() {
  const ctx = useBinder();
  const { entityId } = useParams();
  const setHp = useRunStore((s) => s.setHp);
  const [tab, setTab] = useState<'scheda' | 'storia'>('scheda');
  if (!ctx) return null;
  const { adventure, state, instance, slug } = ctx;
  const c = adventure.characters.find((x) => x.name === entityId);

  if (!c) {
    return (
      <div>
        <Link to={`/run/${instance.id}/characters`}>← Personaggi</Link>
        <p>Personaggio non trovato.</p>
      </div>
    );
  }

  const max = maxHp(c);
  const current = state.hp[c.name] ?? c.hitPoints?.current ?? max;
  const sc = c.spellcasting as
    | { ability?: string; saveDC?: number; attackBonus?: number; cantrips?: string[]; spells?: { name: string; level?: number; description?: string }[] }
    | undefined;
  const pd = c.personalityDetails as
    | { traits?: string[]; ideals?: string; bonds?: string; flaws?: string }
    | undefined;

  return (
    <div>
      <Link to={`/run/${instance.id}/characters`} className={styles.back}>
        ← Tutti i personaggi
      </Link>
      <PageHeader
        eyebrow={`${c.race ?? ''} ${c.class ?? ''}`.trim()}
        title={c.name}
        subtitle={c.background ? `${c.background} · ${c.alignment ?? ''}` : c.alignment}
        actions={<ShowButton slug={slug} image={c.image} title={c.name} instanceId={instance.id} />}
      />

      <div className={shared.detailLayout}>
        <div className={shared.stack}>
          <MediaImage src={mediaUrl(slug, c.image)} alt={c.name} fallback="🛡️" className={styles.bigPortrait} />
          <Card>
            <div className={shared.blockLabel}>Punti Ferita</div>
            <HpControl current={current} max={max} onChange={(v) => setHp(c.name, v)} />
            <div className={styles.combatRow}>
              <div className={styles.combatStat}>
                <span>CA</span>
                <strong>{c.armorClass ?? '—'}</strong>
              </div>
              <div className={styles.combatStat}>
                <span>Velocità</span>
                <strong>{c.speed ?? '—'}</strong>
              </div>
              <div className={styles.combatStat}>
                <span>Dadi Vita</span>
                <strong>{c.hitPoints?.hitDice ?? '—'}</strong>
              </div>
            </div>
          </Card>
          {c.abilities && (
            <Card>
              <div className={shared.blockLabel}>Caratteristiche</div>
              <StatBlock abilities={c.abilities} />
            </Card>
          )}
        </div>

        <div className={shared.stack}>
          <div className={styles.tabs}>
            <button
              className={[styles.tab, tab === 'scheda' ? styles.tabActive : ''].join(' ')}
              onClick={() => setTab('scheda')}
            >
              Scheda
            </button>
            <button
              className={[styles.tab, tab === 'storia' ? styles.tabActive : ''].join(' ')}
              onClick={() => setTab('storia')}
            >
              Storia & Personalità
            </button>
          </div>

          {tab === 'scheda' ? (
            <>
              {!!(c.attacks && c.attacks.length) && (
                <Card>
                  <div className={shared.blockLabel}>Attacchi</div>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Bonus</th>
                        <th>Danno</th>
                        <th>Tipo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.attacks.map((a, i) => (
                        <tr key={i}>
                          <td>{a.name}</td>
                          <td>{a.bonus ?? '—'}</td>
                          <td>{a.damage ?? '—'}</td>
                          <td>{a.type ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              )}

              {sc && (sc.cantrips?.length || sc.spells?.length) && (
                <Card>
                  <div className={shared.blockLabel}>Incantesimi</div>
                  <div className={shared.tagRow}>
                    {sc.ability && <Badge tone="gold">Caratteristica: {sc.ability}</Badge>}
                    {sc.saveDC != null && <Badge>CD {sc.saveDC}</Badge>}
                    {sc.attackBonus != null && <Badge>Attacco +{sc.attackBonus}</Badge>}
                  </div>
                  {!!sc.cantrips?.length && (
                    <p className={shared.entityMeta}>Trucchetti: {sc.cantrips.join(', ')}</p>
                  )}
                  {!!sc.spells?.length && (
                    <ul className={styles.spellList}>
                      {sc.spells.map((s, i) => (
                        <li key={i}>
                          <strong>{s.name}</strong>
                          {s.level != null ? ` (liv. ${s.level})` : ''}
                          {s.description ? ` — ${s.description}` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              )}

              {!!(c.equipment && c.equipment.length) && (
                <Card>
                  <div className={shared.blockLabel}>Equipaggiamento</div>
                  <ul className={styles.bullets}>
                    {c.equipment.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </Card>
              )}

              {!!(c.skills && c.skills.length) && (
                <Card>
                  <div className={shared.blockLabel}>Competenze</div>
                  <div className={shared.tagRow}>
                    {c.skills.map((s) => (
                      <span key={s} className={shared.chip}>
                        {s}
                      </span>
                    ))}
                  </div>
                </Card>
              )}
            </>
          ) : (
            <>
              {c.backstory && (
                <Card>
                  <div className={shared.blockLabel}>Background</div>
                  <MarkdownView>{c.backstory}</MarkdownView>
                </Card>
              )}
              {c.personality && (
                <Card>
                  <div className={shared.blockLabel}>Personalità</div>
                  <MarkdownView>{c.personality}</MarkdownView>
                </Card>
              )}
              {pd && (
                <Card>
                  {pd.ideals && (
                    <p>
                      <strong>Ideali:</strong> {pd.ideals}
                    </p>
                  )}
                  {pd.bonds && (
                    <p>
                      <strong>Legami:</strong> {pd.bonds}
                    </p>
                  )}
                  {pd.flaws && (
                    <p>
                      <strong>Difetti:</strong> {pd.flaws}
                    </p>
                  )}
                </Card>
              )}
              {c.adventureHook && (
                <Card>
                  <div className={shared.blockLabel}>Aggancio all'avventura</div>
                  <MarkdownView>{c.adventureHook}</MarkdownView>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {c.id && <Backlinks adventure={adventure} targetRef={`character:${c.id}`} />}
    </div>
  );
}
